import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';
import { Award, Clock, Shield, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const About = () => {
  const [aboutInfo, setAboutInfo] = useState(null);

  useEffect(() => {
    fetchAboutInfo();
  }, []);

  const fetchAboutInfo = async () => {
    try {
      const response = await axios.get(`${API}/site/about-info`);
      setAboutInfo(response.data);
    } catch (error) {
      console.error('Error fetching about info:', error);
    }
  };

  const values = [
    {
      icon: <Clock className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12" />,
      title: "Trusted Since 1975",
      description: "50 years of excellence in watch retail, serving generations of customers."
    },
    {
      icon: <Shield className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12" />,
      title: "100% Authenticity",
      description: "Every watch comes with manufacturer warranty and authenticity certificate."
    },
    {
      icon: <Award className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12" />,
      title: "Authorized Retailer",
      description: "Official partner for 40+ premium watch brands."
    },
    {
      icon: <Users className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12" />,
      title: "Customer First",
      description: "Thousands of satisfied customers trust us for their watch needs."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section - Reduced height on mobile */}
      <div className="relative h-48 sm:h-64 md:h-80 lg:h-96 bg-gradient-to-r from-black via-gray-900 to-black flex items-center">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=1920&h=800&fit=crop"
            alt="About us"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-2 sm:mb-4">
            {aboutInfo?.title ? (
              <>
                About <span className="text-yellow-500">{aboutInfo.title.replace('About ', '')}</span>
              </>
            ) : (
              <>
                About <span className="text-yellow-500">Jain Watch Company</span>
              </>
            )}
          </h1>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-300 max-w-2xl">
            Your trusted partner for authentic timepieces since 1975
          </p>
        </div>
      </div>

      {/* Our Story */}
      <div className="container mx-auto px-4 py-8 sm:py-12 md:py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-4 sm:mb-6 md:mb-8">Our Story</h2>
          <div className="prose prose-sm sm:prose-base md:prose-lg max-w-none text-gray-700 space-y-3 sm:space-y-4 md:space-y-6">
            {aboutInfo?.description ? (
              aboutInfo.description.split('\n').map((paragraph, idx) => (
                <p key={idx} className="text-sm sm:text-base md:text-lg leading-relaxed">
                  {paragraph}
                </p>
              ))
            ) : (
              <>
                <p className="text-sm sm:text-base md:text-lg leading-relaxed">
                  Founded in 1975, Jain Watch Company has been at the forefront of luxury watch retail in India for nearly five decades. What started as a small watch shop has grown into one of the most trusted names in the industry.
                </p>
                <p className="text-sm sm:text-base md:text-lg leading-relaxed">
                  Our journey began with a simple mission: to provide authentic, high-quality watches to watch enthusiasts and everyday customers alike. Over the years, we've built strong relationships with over 40 premium watch brands.
                </p>
                <p className="text-sm sm:text-base md:text-lg leading-relaxed">
                  Today, Jain Watch Company stands as a testament to quality, authenticity, and customer satisfaction.
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mission & Vision */}
      {(aboutInfo?.mission || aboutInfo?.vision) && (
        <div className="bg-gray-100 py-8 sm:py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 max-w-5xl mx-auto">
              {aboutInfo?.mission && (
                <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-3 md:mb-4 text-yellow-600">Our Mission</h3>
                  <p className="text-gray-700 text-sm sm:text-base leading-relaxed">{aboutInfo.mission}</p>
                </div>
              )}
              {aboutInfo?.vision && (
                <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-3 md:mb-4 text-yellow-600">Our Vision</h3>
                  <p className="text-gray-700 text-sm sm:text-base leading-relaxed">{aboutInfo.vision}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Values */}
      <div className="bg-white py-8 sm:py-12 md:py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-6 sm:mb-8 md:mb-12">Why Choose Us</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 lg:gap-8">
            {values.map((value, idx) => (
              <div key={idx} className="bg-gray-50 rounded-lg p-3 sm:p-4 md:p-6 lg:p-8 text-center hover:shadow-xl transition-shadow duration-300">
                <div className="text-yellow-500 flex justify-center mb-2 sm:mb-3 md:mb-4">
                  {value.icon}
                </div>
                <h3 className="text-xs sm:text-sm md:text-base lg:text-xl font-bold mb-1 sm:mb-2 md:mb-3">{value.title}</h3>
                <p className="text-gray-600 text-[10px] sm:text-xs md:text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Brands */}
      <div className="container mx-auto px-4 py-8 sm:py-12 md:py-16">
        <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-6 sm:mb-8 md:mb-12">Authorized Retailer For</h2>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 sm:gap-3 md:gap-4 lg:gap-6">
          {['Titan', 'Casio', 'Fastrack', 'Guess', 'Timex', 'Raga', 'Logues', 'Zoop', 'TMX', 'Obaque', 'Jean Fendi', 'More...'].map((brand, idx) => (
            <div key={idx} className="bg-white rounded-lg shadow-md p-2 sm:p-3 md:p-4 lg:p-6 flex items-center justify-center hover:shadow-xl transition-shadow duration-300">
              <span className="text-xs sm:text-sm md:text-base lg:text-lg font-bold text-gray-800 text-center">{brand}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Call to Action */}
      <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 py-8 sm:py-12 md:py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-black mb-2 sm:mb-3 md:mb-4">Ready to Find Your Perfect Watch?</h2>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-black/80 mb-4 sm:mb-6 md:mb-8">Explore our extensive collection of authentic timepieces</p>
          <a
            href="/products"
            className="inline-block bg-black hover:bg-gray-800 text-white font-bold py-2.5 sm:py-3 md:py-4 px-6 sm:px-8 md:px-12 rounded-lg transition-all duration-300 transform hover:scale-105 text-sm sm:text-base"
          >
            Shop Now
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default About;
